import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import insuranceSquare from './../../assets/images/insurance-shapes-square.svg';
import insuranceTriangle from './../../assets/images/insurance-shapes-triangle.svg';
import directChart from './../../assets/images/insurance-direct-chart.svg';
import '../insurance.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function InsurancePlanning() {

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');
          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => { ctx.revert(); /* <- cleanup! */ };

  }, []);

  useEffect(() => {
    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="insurance-planning">

        <p id="s1" className="intro" data-aos="fade-up">See how today's insurance companies plan and manage direct mail.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail Expertise</h3>
                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="67" aria-hidden="true">0</span><span class="screen-reader-text">67</span>%</span>
                </span>

                <p>Advanced knowledge</p>
                <p>Insurance companies report high levels of expertise with direct mail, with 67% saying they have advanced knowledge of the medium.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Whether you’re an expert or a beginner, knowing how to create an engaging, actionable mail campaign is crucial. Explore <strong><a href="/../10-steps-direct-mail/">10 easy tips</a></strong> for capturing customers’ attention and discover how today’s brands are making direct mail work for them.</p>
              </div>
            </div>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail Format</h3>
                <div className='progress-bar' data-aos data-aos-id="bar-anim">
                  <div className='progress' data-fill="70"><span class="percentage" data-to="70" aria-hidden="true">0</span><span class="screen-reader-text">70</span>%</div>
                </div>
                <p>Letters</p>
                <p>Insurance companies stand out for the propensity to use letters—rather than postcards, brochures or other formats—in their mail campaigns.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Letters can be the ideal format for conveying more involved or complex information in a personal, easy-to-access way—especially important for critical insurance communications. This step-by-step direct mail tutorial can help you <strong><a href="/../dm-101-tutorial/chapter_4#subsection3">decide on the ideal format</a> </strong> for your next mail campaign.</p>
              </div>
            </div>
          </div>

          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>In-House Mail Management</h3>
                <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                  <div className='progressbar-item'>
                    <span className='label'>Creative execution <strong>in-house</strong></span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="58"><span class="percentage" data-to="58" aria-hidden="true">0</span><span class="screen-reader-text">58</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Sorting, assembling and mailing <strong>in-house</strong></span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="49"><span class="percentage" data-to="49" aria-hidden="true">0</span><span class="screen-reader-text">49</span>%</div>
                    </div>
                  </div>
                </div>
                <p>Out of all the companies surveyed, those in the insurance sector are the most likely to handle direct mail creative execution and sorting, assembling and mailing in-house.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>The <strong><a className='external-link' href="https://gateway.usps.com/eAdmin/view/signin" target="_blank">Business Customer Gateway</a></strong> makes it easy to manage all things direct mail in one centralized location—from tracking and monitoring mailings to managing Mailer IDs and permits.</p>
              </div>
            </div>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Management of Direct Mail</h3>
                <p className="pull-quote">Across industries, businesses typically have either <span data-aos className='highlighter'>in-house or agency-shared responsibility</span> for managing various aspects of direct mail. Few companies depend exclusively on outside agencies.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p><strong><a href="/../questions-to-ask-before-choosing-a-mail-service-provider/">Specialized partners</a></strong> can assist in managing some—or all—aspects of your direct mail campaigns. Mail service providers (MSPs), advertising agencies and printers can help with different needs, such as writing and designing mailpieces, making sure your mailing meets USPS requirements, printing and presorting, and more.</p>
              </div>
            </div>
            
            <div className='personalized-box secondary'>
              <h3>Insurance companies prefer to use letters for direct mail campaigns.</h3>
              <div className='shape-images'>
                <div className='triangle triangle-1'>
                  <img ref={triangle1Ref} src={insuranceTriangle} alt="" />
                </div>
                <div className='square square-1'>
                  <img ref={square1Ref} src={insuranceSquare} alt="" />
                </div>
                <div className='triangle triangle-2'>
                  <img ref={triangle2Ref} src={insuranceTriangle} alt="" />
                </div>
                <div className='square square-2'>
                  <img ref={square2Ref} src={insuranceSquare} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
